import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag, MessageCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";

export default function CartDrawer() {
  const { items, open, setOpen, setQty, removeItem, subtotal, count, lines } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col bg-cream p-0" data-testid="cart-drawer">
        <SheetHeader className="px-5 py-4 border-b border-forest/10">
          <SheetTitle className="font-serif text-2xl text-forest-dark flex items-center gap-2">
            <ShoppingBag size={22} /> Your Cart ({count})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-3">
            <ShoppingBag size={48} className="text-forest/30" />
            <p className="text-charcoal/60">Your cart is empty.</p>
            <Button onClick={() => setOpen(false)} className="bg-forest text-cream rounded-full">Start Shopping</Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 hide-scrollbar">
              {items.map((i) => (
                <div key={i.sku} className="flex gap-3 bg-white rounded-xl p-3 border border-forest/10" data-testid={`cart-item-${i.sku}`}>
                  <img src={i.image} alt={i.name} className="h-16 w-16 rounded-lg object-cover" loading="lazy" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-charcoal truncate">{i.name}</p>
                    <p className="text-xs text-charcoal/60">{i.pack_size}</p>
                    <p className="text-forest font-bold mt-1">{formatINR(i.price)}</p>
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <button onClick={() => removeItem(i.sku)} data-testid={`cart-remove-${i.sku}`} className="text-charcoal/40 hover:text-discount">
                      <Trash2 size={16} />
                    </button>
                    <div className="flex items-center border border-forest/20 rounded-full">
                      <button onClick={() => setQty(i.sku, i.quantity - 1)} data-testid={`cart-dec-${i.sku}`} className="p-1.5"><Minus size={14} /></button>
                      <span className="w-6 text-center text-sm font-semibold" data-testid={`cart-qty-${i.sku}`}>{i.quantity}</span>
                      <button onClick={() => setQty(i.sku, i.quantity + 1)} data-testid={`cart-inc-${i.sku}`} className="p-1.5"><Plus size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-forest/10 p-5 space-y-3 bg-white">
              <div className="flex justify-between text-sm">
                <span className="text-charcoal/70">Subtotal</span>
                <span className="font-bold text-forest-dark" data-testid="cart-subtotal">{formatINR(subtotal)}</span>
              </div>
              <p className="text-xs text-charcoal/50">Delivery & discounts are calculated at checkout.</p>
              <Button onClick={goCheckout} data-testid="cart-drawer-checkout-button" className="w-full bg-forest hover:bg-forest-dark text-cream rounded-full h-12 text-base font-semibold">
                Checkout — {formatINR(subtotal)}
              </Button>
              <Button
                onClick={() => openWhatsApp({ items: lines })}
                variant="outline"
                data-testid="cart-drawer-whatsapp-button"
                className="w-full border-whatsapp text-whatsapp hover:bg-whatsapp/10 rounded-full h-11 font-semibold"
              >
                <MessageCircle size={18} className="mr-2" /> Order on WhatsApp
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
